import { supabase } from './supabase.js';

// ─── Admin check ──────────────────────────────────────────────────────────────
// Change this list to grant admin access to real accounts.
const ADMIN_EMAILS = ['admin@pickasyde.com', 'kingwest.jerry@gmail.com'];

export function isAdmin(user) {
  if (!user) return false;
  return ADMIN_EMAILS.includes(user.email);
}

// ─── Date helpers ─────────────────────────────────────────────────────────────
// Returns today's date as YYYY-MM-DD in Eastern Time (EST/EDT).
function getTodayEST() {
  return new Date()
    .toLocaleDateString('en-CA', { timeZone: 'America/New_York' }); // en-CA gives YYYY-MM-DD
}

// ─── Debates ──────────────────────────────────────────────────────────────────

/**
 * Fetch today's debate (matched by EST date).
 * Returns the debate row, or null if none is scheduled for today.
 */
export async function getTodayDebate() {
  const today = getTodayEST();
  const { data, error } = await supabase
    .from('debates')
    .select('*')
    .eq('date', today)
    .single();

  if (error && error.code !== 'PGRST116') {
    // PGRST116 = "no rows" — not an error we need to throw
    console.error('getTodayDebate error:', error);
    throw error;
  }
  return data ?? null;
}

/**
 * Fetch all past debates (date before today) or explicitly closed ones,
 * newest first (for the Archive screen).
 * A debate belongs in the archive if its date has already passed OR it's
 * been manually marked closed — whichever comes first.
 */
export async function getArchive() {
  const today = getTodayEST();
  const { data, error } = await supabase
    .from('debates')
    .select('*')
    .or(`is_closed.eq.true,date.lt.${today}`)
    .neq('date', today)          // ← NEVER show today in the archive, even if admin closes it early
    .order('date', { ascending: false });

  if (error) {
    console.error('getArchive error:', error);
    throw error;
  }
  return data ?? [];
}

/**
 * Fetch the next upcoming debate (date > today), for the "come back tomorrow" teaser.
 */
export async function getUpcomingDebate() {
  const today = getTodayEST();
  const { data, error } = await supabase
    .from('debates')
    .select('id, question, label_a, label_b, date, image_url_a, image_url_b')
    .gt('date', today)
    .order('date', { ascending: true })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('getUpcomingDebate error:', error);
    return null;
  }
  return data ?? null;
}

// ─── Votes ────────────────────────────────────────────────────────────────────

/**
 * Get the current user's vote for a specific debate.
 * Returns 'A', 'B', or null.
 */
export async function getUserVote(debateId) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('votes')
    .select('id, side')
    .eq('debate_id', debateId)
    .eq('user_id', user.id)
    .maybeSingle();

  if (error) {
    console.error('getUserVote error:', error);
    throw error;
  }
  return data ?? null; // { id, side } or null
}

/**
 * Get total vote counts for a debate (for the vote bar).
 * Returns { countA, countB }.
 */
export async function getVoteCounts(debateId) {
  const { data, error } = await supabase
    .from('votes')
    .select('side')
    .eq('debate_id', debateId);

  if (error) {
    console.error('getVoteCounts error:', error);
    throw error;
  }
  const countA = data.filter(v => v.side === 'A').length;
  const countB = data.filter(v => v.side === 'B').length;
  return { countA, countB };
}

/**
 * Cast a new vote for the current user.
 * Returns the created vote row.
 * Throws a user-friendly error string on unique constraint violation.
 */
export async function castVote(debateId, side) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('votes')
    .insert({ debate_id: debateId, user_id: user.id, side })
    .select()
    .single();

  if (error) {
    if (error.code === '23505') {
      // Unique violation — user already voted in this debate
      throw new Error('You have already voted in this debate. Use changeVote() to change it.');
    }
    console.error('castVote error:', error);
    throw error;
  }
  return data;
}

/**
 * Change an existing vote to a new side.
 * voteId is the UUID of the existing vote row.
 */
export async function changeVote(voteId, side) {
  const { data, error } = await supabase
    .from('votes')
    .update({ side })
    .eq('id', voteId)
    .select()
    .single();

  if (error) {
    if (error.code === '42501') {
      throw new Error('Cannot change vote — debate is closed.');
    }
    console.error('changeVote error:', error);
    throw error;
  }
  return data;
}

/**
 * Execute a full side switch:
 *  1. Update vote (new side + increment switch_count to 1)
 *  2. Mark user's existing active comment as 'historical'
 *  3. Insert a side_switch_event record
 * Returns { voteRow, switchEvent }
 */
export async function executeSideSwitch({ voteId, debateId, previousSide, newSide, persuadingCommentId, switchReasonText }) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // 1. Update vote
  const { data: voteRow, error: vErr } = await supabase
    .from('votes')
    .update({ side: newSide, switch_count: 1 })
    .eq('id', voteId)
    .select()
    .single();
  if (vErr) { console.error('executeSideSwitch vote error:', vErr); throw vErr; }

  // 2. Mark any active comment on old side as historical
  await supabase
    .from('comments')
    .update({ comment_status: 'historical' })
    .eq('debate_id', debateId)
    .eq('user_id', user.id)
    .eq('comment_status', 'active');

  // 3. Insert side_switch_event
  const { data: switchEvent, error: sErr } = await supabase
    .from('side_switch_events')
    .insert({
      user_id: user.id,
      debate_id: debateId,
      previous_side: previousSide,
      new_side: newSide,
      persuading_comment_id: persuadingCommentId || null,
      switch_reason_text: switchReasonText || null,
    })
    .select()
    .single();
  if (sErr) { console.error('executeSideSwitch event error:', sErr); throw sErr; }

  return { voteRow, switchEvent };
}

/**
 * Get how many times the current user has switched sides in a debate.
 * Returns a count (0, 1, or 2). Max 2 switches allowed.
 */
export async function hasUserSwitched(debateId) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return 0;

  const { data, error } = await supabase
    .from('side_switch_events')
    .select('id')
    .eq('debate_id', debateId)
    .eq('user_id', user.id);

  if (error) { console.error('hasUserSwitched error:', error); return 0; }
  return data?.length ?? 0;
}

/**
 * Log a persuasion signal (user tapped "This changed my mind").
 * Called BEFORE the confirmation modal — every tap is captured for analytics.
 */
export async function logPersuasionSignal(debateId, commentId) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const { error } = await supabase
    .from('persuasion_signal_events')
    .insert({ user_id: user.id, debate_id: debateId, comment_id: commentId });

  if (error && error.code !== '23505') {
    console.error('logPersuasionSignal error:', error);
  }
}

/**
 * Fetch comments including comment_status (active vs historical).
 * Use this instead of getComments going forward.
 */
export async function getCommentsWithStatus(debateId) {
  const { data, error } = await supabase
    .from('comments')
    .select(`
      id,
      debate_id,
      user_id,
      side,
      text,
      upvote_count,
      created_at,
      comment_status,
      user_profiles ( display_name, is_ai_seed )
    `)
    .eq('debate_id', debateId)
    .order('created_at', { ascending: false });

  if (error) { console.error('getCommentsWithStatus error:', error); throw error; }
  return data ?? [];
}

// ─── Comments ─────────────────────────────────────────────────────────────────

/**
 * Fetch all comments for a debate, newest first.
 * Joins user_profiles to get display names.
 */
export async function getComments(debateId) {
  const { data, error } = await supabase
    .from('comments')
    .select(`
      id,
      debate_id,
      user_id,
      side,
      text,
      upvote_count,
      created_at,
      user_profiles ( display_name, is_ai_seed )
    `)
    .eq('debate_id', debateId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('getComments error:', error);
    throw error;
  }
  return data ?? [];
}

/**
 * Post a new comment. The user must have voted before calling this.
 * text — raw comment string (should be moderated before calling).
 * Returns the created comment row.
 */
export async function postComment(debateId, side, text) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('comments')
    .insert({ debate_id: debateId, user_id: user.id, side, text })
    .select(`
      id,
      debate_id,
      user_id,
      side,
      text,
      upvote_count,
      created_at,
      user_profiles ( display_name, is_ai_seed )
    `)
    .single();

  if (error) {
    console.error('postComment error:', error);
    throw error;
  }
  return data;
}

// ─── Upvotes ──────────────────────────────────────────────────────────────────

/**
 * Upvote a comment. The DB trigger will increment comments.upvote_count.
 * Returns the created upvote row.
 * Throws a user-friendly error on duplicate upvote.
 */
export async function upvoteComment(commentId) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('upvotes')
    .insert({ comment_id: commentId, user_id: user.id })
    .select()
    .single();

  if (error) {
    if (error.code === '23505') {
      throw new Error('You have already upvoted this comment.');
    }
    console.error('upvoteComment error:', error);
    throw error;
  }
  return data;
}

/**
 * Remove an upvote from a comment. The DB trigger will decrement comments.upvote_count.
 */
export async function removeUpvote(commentId) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { error } = await supabase
    .from('upvotes')
    .delete()
    .eq('comment_id', commentId)
    .eq('user_id', user.id);

  if (error) {
    console.error('removeUpvote error:', error);
    throw error;
  }
}

/**
 * Get the set of comment IDs the current user has upvoted for a given debate.
 * Returns a Set of comment UUIDs.
 */
export async function getUserUpvotes(debateId) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return new Set();

  // Get all comment IDs for this debate first
  const { data: commentIds, error: cErr } = await supabase
    .from('comments')
    .select('id')
    .eq('debate_id', debateId);

  if (cErr || !commentIds?.length) return new Set();

  const ids = commentIds.map(c => c.id);
  const { data, error } = await supabase
    .from('upvotes')
    .select('comment_id')
    .eq('user_id', user.id)
    .in('comment_id', ids);

  if (error) {
    console.error('getUserUpvotes error:', error);
    return new Set();
  }
  return new Set(data.map(u => u.comment_id));
}

// ─── User profile ─────────────────────────────────────────────────────────────

/**
 * Fetch the current user's profile (streak, display name, etc.).
 */
export async function getUserProfile() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('id', user.id)
    .single();

  if (error) {
    console.error('getUserProfile error:', error);
    return null;
  }
  return data;
}

/**
 * Update the current user's display name.
 */
export async function updateDisplayName(name) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { error } = await supabase
    .from('user_profiles')
    .update({ display_name: name })
    .eq('id', user.id);

  if (error) throw error;
}

// ─── Real-time subscription ───────────────────────────────────────────────────

/**
 * Subscribe to new comments for a debate.
 * onNewComment receives a fully-shaped comment object.
 * Returns the channel — call channel.unsubscribe() on cleanup.
 */
export function subscribeToComments(debateId, onNewComment) {
  const channel = supabase
    .channel(`comments:${debateId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'comments',
        filter: `debate_id=eq.${debateId}`,
      },
      async (payload) => {
        // Fetch the full comment with joined profile so it matches getCommentsWithStatus() shape
        const { data } = await supabase
          .from('comments')
          .select(`
            id,
            debate_id,
            user_id,
            side,
            text,
            upvote_count,
            created_at,
            comment_status,
            user_profiles ( display_name, is_ai_seed )
          `)
          .eq('id', payload.new.id)
          .single();

        if (data) onNewComment(data);
      }
    )
    .subscribe();

  return channel;
}

/**
 * Subscribe to upvote_count changes on comments for a debate.
 * onUpdate receives { commentId, newCount }.
 */
export function subscribeToUpvotes(debateId, onUpdate) {
  const channel = supabase
    .channel(`upvotes:${debateId}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'comments',
        filter: `debate_id=eq.${debateId}`,
      },
      (payload) => {
        onUpdate({ commentId: payload.new.id, newCount: payload.new.upvote_count });
      }
    )
    .subscribe();

  return channel;
}

/**
 * Update the text of an existing comment (owner or admin).
 * Returns the updated comment row.
 */
export async function updateComment(commentId, newText) {
  const { data, error } = await supabase
    .from('comments')
    .update({ text: newText })
    .eq('id', commentId)
    .select(`
      id,
      debate_id,
      user_id,
      side,
      text,
      upvote_count,
      created_at,
      user_profiles ( display_name, is_ai_seed )
    `)
    .maybeSingle();

  if (error) {
    console.error('updateComment error:', error);
    throw error;
  }
  if (!data) throw new Error('Comment not found or permission denied');
  return data;
}

export async function deleteComment(commentId) {
  const { error } = await supabase.from('comments').delete().eq('id', commentId);
  if (error) { console.error('deleteComment error:', error); throw error; }
}

// ─── Admin / Backoffice ───────────────────────────────────────────────────────

/**
 * Fetch all debates ordered by date desc (admin use — includes future ones).
 */
export async function getAllDebatesAdmin() {
  const { data, error } = await supabase
    .from('debates')
    .select('*')
    .order('date', { ascending: false });
  if (error) { console.error('getAllDebatesAdmin error:', error); throw error; }
  return data ?? [];
}

/**
 * Create a new debate question.
 */
export async function createDebate({ question, date, label_a, label_b, image_url_a, image_url_b, sponsor_name, sponsor_tagline, sponsor_logo_letter, sponsor_color }) {
  const { data, error } = await supabase
    .from('debates')
    .insert({ question, date, label_a, label_b, image_url_a: image_url_a||null, image_url_b: image_url_b||null, sponsor_name, sponsor_tagline, sponsor_logo_letter, sponsor_color, is_closed: false })
    .select()
    .single();
  if (error) { console.error('createDebate error:', error); throw error; }
  return data;
}

/**
 * Update an existing debate (question text, labels, results, etc.).
 */
export async function updateDebate(id, fields) {
  const { data, error } = await supabase
    .from('debates')
    .update(fields)
    .eq('id', id)
    .select()
    .maybeSingle();
  if (error) { console.error('updateDebate error:', error); throw error; }
  if (!data) throw new Error('Debate not found or permission denied');
  return data;
}

/**
 * Delete a debate (admin only).
 */
export async function deleteDebate(id) {
  const { error } = await supabase.from('debates').delete().eq('id', id);
  if (error) { console.error('deleteDebate error:', error); throw error; }
}

/**
 * Fetch recent comments across all debates for moderation.
 */
export async function getRecentCommentsAdmin(limit = 50) {
  const { data, error } = await supabase
    .from('comments')
    .select(`
      id, debate_id, user_id, side, text, upvote_count, created_at, comment_status,
      user_profiles ( display_name, is_ai_seed ),
      debates ( question, date )
    `)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) { console.error('getRecentCommentsAdmin error:', error); throw error; }
  return data ?? [];
}

// ─── Voter data (admin only) ──────────────────────────────────────────────────

/**
 * Fetch real (non-AI) voters with their email addresses.
 * Requires admin_get_voters() RPC function in Supabase (see supabase_migration_v3.sql).
 */
export async function getVoterEmails() {
  const { data, error } = await supabase.rpc('admin_get_voters');
  if (error) { console.error('getVoterEmails error:', error); throw error; }
  return data ?? [];
}

/**
 * Fetch a voter's debate vote history (admin only).
 */
export async function getVoterHistory(userId) {
  const { data, error } = await supabase.rpc('admin_get_voter_history', { p_user_id: userId });
  if (error) { console.error('getVoterHistory error:', error); throw error; }
  return data ?? [];
}

/**
 * Fetch display names of real voters for a debate, grouped by side.
 * Public — uses get_debate_voter_names RPC.
 */
export async function getDebateVoterNames(debateId) {
  const { data, error } = await supabase.rpc('get_debate_voter_names', { p_debate_id: debateId });
  if (error) { console.error('getDebateVoterNames error:', error); return []; }
  return data ?? [];
}

/**
 * Read an app setting by key (e.g. 'comment_required').
 */
export async function getAppSetting(key) {
  const { data, error } = await supabase
    .from('app_settings')
    .select('value')
    .eq('key', key)
    .maybeSingle();
  if (error) { console.error('getAppSetting error:', error); return null; }
  return data?.value ?? null;
}

/**
 * Update an app setting (admin only).
 */
export async function setAppSetting(key, value) {
  const { error } = await supabase
    .from('app_settings')
    .upsert({ key, value, updated_at: new Date().toISOString() }, { onConflict: 'key' });
  if (error) { console.error('setAppSetting error:', error); throw error; }
}

/**
 * Send the daily debate email to a specific list of addresses.
 * Can include custom subject, intro, and CTA text.
 */
export async function sendEmailToAddresses({ emails, customSubject, customIntro, customCta }) {
  if (!emails?.length) throw new Error('No emails provided');
  const { error } = await supabase.functions.invoke('daily-email', {
    body: { targetEmails: emails, customSubject, customIntro, customCta },
  });
  if (error) throw error;
}

// ─── AI Seed (admin only) ─────────────────────────────────────────────────────

/**
 * Seed an AI-generated comment into the DB under a random AI persona.
 * The text should already be generated by moderation.js / generateAIComment().
 * aiPersonas is the array of { id, name } seed users stored in user_profiles.
 */
export async function seedAIComment(debateId, side, text, aiPersonaId) {
  const { data, error } = await supabase
    .from('comments')
    .insert({
      debate_id: debateId,
      user_id: aiPersonaId,
      side,
      text,
    })
    .select(`
      id,
      debate_id,
      user_id,
      side,
      text,
      upvote_count,
      created_at,
      user_profiles ( display_name, is_ai_seed )
    `)
    .single();

  if (error) {
    console.error('seedAIComment error:', error);
    throw error;
  }
  return data;
}

/**
 * Fetch all AI persona profiles.
 */
export async function getAIPersonas() {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('id, display_name')
    .eq('is_ai_seed', true);

  if (error) {
    console.error('getAIPersonas error:', error);
    return [];
  }
  return data ?? [];
}

/**
 * Fetch all comments + their debate info for a given display_name.
 * Used by the user profile view.
 */
export async function getCommentsByDisplayName(displayName) {
  // First resolve the user_id from display_name
  const { data: profile, error: profErr } = await supabase
    .from('user_profiles')
    .select('id')
    .eq('display_name', displayName)
    .maybeSingle();
  if (profErr || !profile) return [];

  const today = getTodayEST();
  // Use !inner join so PostgREST filters on the joined table server-side,
  // preventing future debate comments from leaking to the client entirely.
  const { data, error } = await supabase
    .from('comments')
    .select('id, side, text, upvote_count, created_at, debate_id, debates!inner(question, date, label_a, label_b, final_pct_a)')
    .eq('user_id', profile.id)
    .lte('debates.date', today)
    .order('created_at', { ascending: false });
  if (error) { console.error('getCommentsByDisplayName error:', error); return []; }
  return data ?? [];
}

/**
 * Get the count of minds changed (side_switch_events) for a debate.
 * Returns the count of actual switch events.
 */
export async function getMindsChangedCount(debateId) {
  const { data, error } = await supabase
    .from('side_switch_events')
    .select('id')
    .eq('debate_id', debateId);

  if (error) {
    console.error('getMindsChangedCount error:', error);
    return 0;
  }
  return data?.length ?? 0;
}
